/* ==========================================================================
   IZAR — إزار · theme.js
   Vanilla JS, no dependencies, loaded with `defer`.
   Progressive enhancement: navigation, product forms, cart page and filters
   all work without JS. JS adds the AJAX cart, drawers, live variant state,
   and the Order Request submission (which needs a backend endpoint).
   ========================================================================== */
(() => {
  'use strict';

  const IZ = window.IZAR || {};
  const S = IZ.strings || {};
  const R = IZ.routes || {};
  const ROOT = String(R.root || '/').replace(/\/?$/, '/');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  /* ---------- Safe storage (private mode can throw) ---------- */
  const store = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (_) { /* ignore */ } },
    remove(key) { try { window.sessionStorage.removeItem(key); } catch (_) { /* ignore */ } }
  };

  /* ---------- Money ---------- */
  function formatMoney(cents, format = IZ.moneyFormat || '{{amount}}') {
    const value = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, decimals = 2, thousands = ',', decimal = '.') => {
      const [int, frac] = (n / 100).toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const match = String(format).match(pattern);
    if (!match) return fmt(value);
    let out;
    switch (match[1]) {
      case 'amount_no_decimals': out = fmt(value, 0); break;
      case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = fmt(value, 0, ' '); break;
      case 'amount_with_space_separator': out = fmt(value, 2, ' ', ','); break;
      default: out = fmt(value, 2);
    }
    return String(format).replace(pattern, out);
  }

  /* ---------- Toast (role=status, announced by screen readers) ---------- */
  let toastTimer;
  function toast(message, type = 'info') {
    const el = $('#IzarToast');
    if (!el || !message) return;
    el.textContent = message;
    el.dataset.type = type;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, type === 'error' ? 6000 : 3200);
  }

  /* ---------- Dialogs: drawers & modals (native <dialog>: focus trap + Esc) ---------- */
  const isDialog = (el) => !!el && el.tagName === 'DIALOG';

  function openDialog(dialog, opener) {
    if (!isDialog(dialog)) return;
    clearTimeout(dialog._closeTimer);
    dialog._opener = opener || document.activeElement;
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  }

  function closeDialog(dialog) {
    if (!isDialog(dialog) || !dialog.open) return;
    dialog.classList.remove('is-open');
    const finish = () => {
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      if (!$('dialog[open]')) document.documentElement.classList.remove('is-locked');
      const opener = dialog._opener;
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
    };
    dialog._closeTimer = setTimeout(finish, reducedMotion() ? 0 : 300);
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (isDialog(dialog)) { e.preventDefault(); openDialog(dialog, opener); }
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) { closeDialog(closer.closest('dialog')); return; }
    // Click on the backdrop (the dialog element itself, outside its inner panel)
    if (isDialog(e.target)) closeDialog(e.target);
  });

  document.addEventListener('cancel', (e) => {
    if (isDialog(e.target)) { e.preventDefault(); closeDialog(e.target); }
  }, true);

  /* ---------- Cart API ---------- */
  function updateCount(count) {
    if (typeof count !== 'number' || Number.isNaN(count)) return;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  function renderSections(sections) {
    Object.entries(sections || {}).forEach(([id, html]) => {
      if (!html) return;
      const host = $$('[data-cart-section]').find((el) => el.dataset.cartSection === id);
      if (!host) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-replace]');
      const current = host.matches('[data-cart-replace]') ? host : $('[data-cart-replace]', host);
      if (!fresh || !current) return;

      const active = document.activeElement;
      const focusKey = active && active.dataset ? active.dataset.focusKey : null;
      const hadFocusInside = current.contains(active);

      current.innerHTML = fresh.innerHTML;
      current.dataset.cartCountSource = fresh.dataset.cartCountSource || '0';
      updateCount(Number(current.dataset.cartCountSource));

      if (focusKey) {
        const target = $$('[data-focus-key]', current).find((el) => el.dataset.focusKey === focusKey);
        if (target) { target.focus(); return; }
      }
      if (hadFocusInside) {
        const fallback = $('[data-close-dialog]', current) || $('h1, h2', current);
        if (fallback) { if (!fallback.hasAttribute('tabindex') && !/^(A|BUTTON)$/.test(fallback.tagName)) fallback.setAttribute('tabindex', '-1'); fallback.focus(); }
      }
    });
  }

  const Cart = {
    sectionIds() {
      return $$('[data-cart-section]').map((el) => el.dataset.cartSection).filter(Boolean);
    },
    async post(path, body) {
      const ids = this.sectionIds();
      if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
      let res;
      try {
        res = await fetch(ROOT + path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
      } catch (_) {
        const err = new Error('network'); err.code = 'network'; throw err;
      }
      let data = null;
      try { data = await res.json(); } catch (_) { /* empty */ }
      if (!res.ok) { const err = new Error('cart'); err.status = res.status; err.data = data; throw err; }
      return data;
    },
    async add(items) {
      const data = await this.post('cart/add.js', { items });
      if (data && data.sections) renderSections(data.sections);
      else await this.refresh();
      return data;
    },
    async change(line, quantity) {
      const data = await this.post('cart/change.js', { line, quantity });
      if (data && data.sections) renderSections(data.sections);
      if (data) updateCount(data.item_count);
      return data;
    },
    async get() {
      let res;
      try { res = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }); }
      catch (_) { const err = new Error('network'); err.code = 'network'; throw err; }
      if (!res.ok) { const err = new Error('cart'); err.status = res.status; throw err; }
      return res.json();
    },
    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`);
      if (res.ok) renderSections(await res.json());
    }
  };

  /* ---------- Buttons ---------- */
  function setLoading(buttons, loading) {
    buttons.filter(Boolean).forEach((btn) => {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', String(loading));
      if (loading) { btn.dataset.wasDisabled = btn.disabled ? '1' : ''; btn.disabled = true; }
      else { btn.disabled = btn.dataset.wasDisabled === '1'; btn.removeAttribute('aria-busy'); }
    });
  }

  function showFormError(form, message) {
    const el = form && $('[data-form-error]', form);
    if (el) { el.textContent = message; el.hidden = false; }
    else toast(message, 'error');
  }

  /* ---------- Quantity steppers (product + cart) ---------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const wrap = step.closest('[data-qty]');
    const input = wrap && $('input', wrap);
    if (!input) return;
    const min = input.min !== '' ? Number(input.min) : 0;
    const max = input.max !== '' ? Number(input.max) : Infinity;
    const current = parseInt(input.value, 10) || 0;
    const next = Math.min(max, Math.max(min, current + Number(step.dataset.qtyStep)));
    if (next === current) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart line updates (drawer + cart page) ---------- */
  const lineTimers = {};
  async function changeLine(line, quantity, source) {
    const row = source && source.closest('[data-cart-line]');
    if (row) row.setAttribute('aria-busy', 'true');
    try {
      await Cart.change(line, quantity);
      toast(quantity === 0 ? S.removed : S.updated);
    } catch (err) {
      if (row) row.removeAttribute('aria-busy');
      toast(err.status === 422 ? S.quantityError : (err.code === 'network' ? S.networkError : S.cartError), 'error');
      Cart.refresh().catch(() => {});
    }
  }

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove || !window.fetch) return;
    e.preventDefault();
    changeLine(Number(remove.dataset.cartRemove), 0, remove);
  });

  document.addEventListener('change', (e) => {
    const target = e.target;

    // Cart line quantity
    const qtyWrap = target.closest && target.closest('[data-qty][data-line]');
    if (qtyWrap && target.matches('input')) {
      const line = Number(qtyWrap.dataset.line);
      clearTimeout(lineTimers[line]);
      lineTimers[line] = setTimeout(() => {
        changeLine(line, Math.max(0, parseInt(target.value, 10) || 0), target);
      }, 400);
      return;
    }

    // Collection sorting
    if (target.matches('[data-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
      return;
    }

    // Collection filters (auto-apply)
    if (target.matches('input[type="checkbox"]') && target.form && target.form.matches('[data-filter-form]')) {
      if (typeof target.form.requestSubmit === 'function') target.form.requestSubmit();
      else target.form.submit();
    }
  });

  /* ---------- Add to cart (product page + quick add) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[data-ajax-cart]') || !window.fetch) return;
    e.preventDefault();

    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;

    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    if (!id) { showFormError(form, S.selectSize); return; }
    const quantity = Math.max(1, parseInt(fd.get('quantity'), 10) || 1);

    const buttons = [e.submitter, $('[type="submit"]', form)]
      .concat(form.id ? $$(`[type="submit"][form="${form.id}"]`) : [])
      .filter((b, i, arr) => b && arr.indexOf(b) === i);

    setLoading(buttons, true);
    try {
      await Cart.add([{ id, quantity }]);
      const drawer = $('#CartDrawer');
      if (IZ.cartType === 'page' || !isDialog(drawer)) { window.location.href = R.cart; return; }
      openDialog(drawer, e.submitter || buttons[0]);
    } catch (err) {
      let msg = S.addError;
      if (err.status === 422) msg = S.quantityError;
      else if (err.code === 'network') msg = S.networkError;
      showFormError(form, msg);
    } finally {
      setLoading(buttons, false);
    }
  });

  /* ---------- Product page: variants, gallery, sticky bar ---------- */
  function initProduct(root) {
    const dataEl = $('[data-product-json]', root);
    if (!dataEl || root.dataset.ready) return;
    root.dataset.ready = '1';

    let product;
    try { product = JSON.parse(dataEl.textContent); } catch (_) { return; }

    const form = document.getElementById(root.dataset.formId);
    const idInput = form && $('[data-variant-id]', form);
    if (idInput && idInput.dataset.name) idInput.name = idInput.dataset.name; // enables JS-managed variant id
    const fieldsets = $$('[data-variant-picker] [data-option-index]', root);
    const atcButtons = () => $$('[data-add-to-cart]', root);

    const selectedValues = () => fieldsets.map((fs) => {
      const checked = $('input:checked', fs);
      return checked ? checked.value : null;
    });
    const findVariant = (values) => product.variants.find((v) => v.options.every((opt, i) => opt === values[i]));

    function markAvailability(values) {
      fieldsets.forEach((fs, index) => {
        $$('[data-option-input]', fs).forEach((input) => {
          const test = values.slice();
          test[index] = input.value;
          const match = findVariant(test);
          const available = !!(match && match.available);
          const chip = input.nextElementSibling;
          if (!chip) return;
          chip.classList.toggle('is-unavailable', !available);
          let sr = $('.visually-hidden', chip);
          if (!available && !sr) {
            sr = document.createElement('span');
            sr.className = 'visually-hidden';
            sr.textContent = ' (' + S.soldOut + ')';
            chip.appendChild(sr);
          } else if (available && sr) sr.remove();
        });
      });
    }

    function setButtons(variant) {
      atcButtons().forEach((btn) => {
        const label = $('[data-atc-label]', btn);
        let text = S.addToCart;
        let disabled = false;
        if (!variant) { text = S.unavailable; disabled = true; }
        else if (!variant.available) { text = S.soldOut; disabled = true; }
        btn.disabled = disabled;
        if (disabled) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
        if (label) label.textContent = text;
      });
    }

    function updatePrice(variant) {
      const wrap = $('[data-product-price]', root);
      if (wrap) {
        const current = $('[data-price-current]', wrap);
        const compare = $('[data-price-compare]', wrap);
        const price = $('[data-price]', wrap);
        if (current) current.textContent = formatMoney(variant.price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (compare) {
          compare.hidden = !onSale;
          const s = $('s', compare);
          if (s && onSale) s.textContent = formatMoney(variant.compare_at_price);
        }
        if (price) price.classList.toggle('price--sale', !!onSale);
      }
      const stickyPrice = $('[data-sticky-price]', root);
      const stickyVariant = $('[data-sticky-variant]', root);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title;
    }

    function updateMedia(variant) {
      if (!variant.featured_media) return;
      const item = $(`[data-media-id="${variant.featured_media.id}"]`, root);
      if (!item) return;
      $$('.gallery__item.is-active', root).forEach((el) => el.classList.remove('is-active'));
      item.classList.add('is-active');
      if (isMobile()) item.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    function updateUrl(variant) {
      if (!window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function update() {
      const values = selectedValues();
      fieldsets.forEach((fs, i) => {
        const label = $('[data-option-selected]', fs);
        if (label) label.textContent = values[i] || '';
      });
      markAvailability(values);
      const variant = findVariant(values);
      if (idInput) idInput.value = variant ? variant.id : '';
      setButtons(variant);
      const err = form && $('[data-form-error]', form);
      if (err) err.hidden = true;
      if (variant) { updatePrice(variant); updateMedia(variant); updateUrl(variant); }
    }

    fieldsets.forEach((fs) => fs.addEventListener('change', update));
    if (fieldsets.length) markAvailability(selectedValues());

    // Gallery position counter (mobile slider)
    const gallery = $('[data-gallery]', root);
    const counter = $('[data-gallery-current]', root);
    if (gallery && counter && 'IntersectionObserver' in window) {
      const items = $$('.gallery__item', gallery);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) counter.textContent = String(items.indexOf(entry.target) + 1);
        });
      }, { root: gallery, threshold: 0.6 });
      items.forEach((item) => io.observe(item));
    }

    // Sticky add-to-cart (mobile): visible once the main button has scrolled past
    const sticky = $('[data-sticky-atc]', root);
    const mainAtc = $('[data-main-atc]', root);
    if (sticky && mainAtc && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', past && isMobile());
        sticky.setAttribute('aria-hidden', String(!(past && isMobile())));
        $$('button', sticky).forEach((b) => { b.tabIndex = past ? 0 : -1; });
      });
      io.observe(mainAtc);
    }
  }

  /* ---------- Order Request ---------- */
  const toLatinDigits = (value) => String(value || '')
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));

  function normalizePhone(value) {
    let p = toLatinDigits(value).replace(/[\s\-().]/g, '');
    if (p.startsWith('+20')) p = '0' + p.slice(3);
    else if (p.startsWith('0020')) p = '0' + p.slice(4);
    else if (/^20\d{10}$/.test(p)) p = '0' + p.slice(2);
    return p;
  }
  const isEgyptMobile = (p) => /^01[0125]\d{8}$/.test(p);
  const isInternational = (p) => /^\+\d{8,15}$/.test(p);

  function newRequestId() {
    const d = new Date();
    const ymd = String(d.getFullYear()).slice(2) + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const bytes = (window.crypto && window.crypto.getRandomValues)
      ? window.crypto.getRandomValues(new Uint8Array(5))
      : Array.from({ length: 5 }, () => Math.floor(Math.random() * 256));
    let rand = '';
    Array.from(bytes).forEach((b) => { rand += alphabet[b % alphabet.length]; });
    return `IZR-${ymd}-${rand}`;
  }

  function initOrderForm() {
    const form = $('[data-order-form]');
    if (!form) return;

    const submitBtn = $('[data-order-submit]');
    const submitLabel = submitBtn && $('.button__label', submitBtn);
    const errorBox = $('[data-order-error]');
    const errorText = $('[data-order-error-text]');
    const waLink = $('[data-order-whatsapp]');
    const sameAsPhone = $('[data-same-as-phone]', form);
    const V = S.validation || {};
    const FIELDS = ['full_name', 'phone', 'whatsapp', 'governorate', 'city', 'address'];
    let attempted = false;
    let submitting = false;
    let finished = false;

    const val = (name) => (form.elements[name] ? String(form.elements[name].value || '').trim() : '');

    const rules = {
      full_name: () => (val('full_name').replace(/\s+/g, ' ').split(' ').filter((w) => w.length > 0).length >= 2 && val('full_name').length >= 5 ? '' : V.full_name),
      phone: () => {
        const raw = toLatinDigits(val('phone')).replace(/[\s\-().]/g, '');
        const p = normalizePhone(raw);
        return isEgyptMobile(p) || (raw.startsWith('+') && !raw.startsWith('+20') && isInternational(raw)) ? '' : V.phone;
      },
      whatsapp: () => {
        if (!val('whatsapp')) return '';
        const raw = toLatinDigits(val('whatsapp')).replace(/[\s\-().]/g, '');
        const p = normalizePhone(raw);
        return isEgyptMobile(p) || (raw.startsWith('+') && isInternational(raw)) ? '' : V.whatsapp;
      },
      governorate: () => (val('governorate') ? '' : V.governorate),
      city: () => (val('city').length >= 2 ? '' : V.city),
      address: () => (val('address').length >= 8 ? '' : V.address)
    };

    function setFieldError(name, message) {
      const wrap = $(`[data-field="${name}"]`, form);
      const input = form.elements[name];
      const error = $(`#OR-${name}-error`, form);
      if (!wrap || !input) return;
      wrap.classList.toggle('is-invalid', !!message);
      if (message) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
      if (error) { error.textContent = message || ''; error.hidden = !message; }
    }

    function validateField(name) {
      const message = rules[name] ? rules[name]() : '';
      setFieldError(name, message);
      return !message;
    }

    FIELDS.forEach((name) => {
      const input = form.elements[name];
      if (!input) return;
      input.addEventListener('blur', () => { if (attempted || input.value) validateField(name); });
      input.addEventListener('input', () => {
        if (input.getAttribute('aria-invalid') === 'true') validateField(name);
      });
      input.addEventListener('change', () => { if (attempted) validateField(name); });
    });

    // WhatsApp = phone shortcut
    if (sameAsPhone && form.elements.whatsapp && form.elements.phone) {
      const sync = () => { if (sameAsPhone.checked) form.elements.whatsapp.value = form.elements.phone.value; };
      sameAsPhone.addEventListener('change', () => {
        form.elements.whatsapp.readOnly = sameAsPhone.checked;
        sync();
        if (attempted) validateField('whatsapp');
      });
      form.elements.phone.addEventListener('input', sync);
    }

    function setSubmitting(on) {
      if (!submitBtn) return;
      submitBtn.disabled = on;
      submitBtn.setAttribute('aria-busy', String(on));
      if (submitLabel) submitLabel.textContent = on ? S.submitting : S.submit;
    }

    function hideError() {
      if (errorBox) errorBox.hidden = true;
      if (waLink) waLink.hidden = true;
    }

    function whatsappText(payload) {
      const c = payload.customer;
      const lines = [
        S.whatsappIntro,
        `#${payload.order_request_id}`,
        `${c.full_name} — ${c.phone}`,
        `${c.governorate} — ${c.city}`,
        c.address,
        ''
      ];
      payload.order.items.forEach((i) => {
        lines.push(`• ${i.product_title}${i.variant_title ? ` (${i.variant_title})` : ''} × ${i.quantity} = ${formatMoney(Math.round(i.line_total * 100))}`);
      });
      lines.push('', `= ${formatMoney(Math.round(payload.order.total * 100))}`);
      if (c.notes) lines.push('', c.notes);
      return lines.join('\n');
    }

    function showError(message, payload) {
      if (!errorBox) { toast(message, 'error'); return; }
      if (errorText) errorText.textContent = message;
      errorBox.hidden = false;
      const order = IZ.order || {};
      if (waLink && payload && order.whatsappFallback && order.whatsapp) {
        waLink.href = `https://wa.me/${encodeURIComponent(order.whatsapp)}?text=${encodeURIComponent(whatsappText(payload))}`;
        waLink.hidden = false;
      }
    }

    function requestId() {
      let id = store.get('izar:pendingRequestId');
      if (!id) { id = newRequestId(); store.set('izar:pendingRequestId', id); }
      return id;
    }

    function buildPayload(cart) {
      return {
        label: 'IZAR Order Request',
        source: 'IZAR website',
        order_request_id: requestId(),
        timestamp: new Date().toISOString(),
        customer: {
          full_name: val('full_name').replace(/\s+/g, ' '),
          phone: normalizePhone(val('phone')),
          whatsapp: val('whatsapp') ? normalizePhone(val('whatsapp')) : '',
          governorate: val('governorate'),
          city: val('city'),
          address: val('address'),
          notes: val('notes')
        },
        order: {
          currency: cart.currency,
          item_count: cart.item_count,
          subtotal: cart.items_subtotal_price / 100,
          total: cart.total_price / 100,
          items: cart.items.map((i) => ({
            product_id: i.product_id,
            variant_id: i.variant_id,
            product_title: i.product_title,
            variant_title: i.variant_title || '',
            options: (i.options_with_values || []).map((o) => ({ name: o.name, value: o.value })),
            sku: i.sku || '',
            quantity: i.quantity,
            price: i.final_price / 100,
            line_total: i.final_line_price / 100,
            url: i.url
          }))
        },
        store: {
          shop: IZ.shop,
          cart_token: cart.token,
          page_url: window.location.href,
          locale: document.documentElement.lang || 'ar'
        },
        company: val('company') // honeypot — must be empty
      };
    }

    async function sendOrder(payload) {
      const order = IZ.order || {};
      if (order.demoMode) {
        // Prototype-only simulation. Nothing is stored. A visible notice is shown while enabled.
        await new Promise((resolve) => setTimeout(resolve, 700));
        return { ok: true, demo: true, order_request_id: payload.order_request_id };
      }
      const endpoint = String(order.endpoint || '').trim();
      if (!endpoint) { const e = new Error('not configured'); e.code = 'not_configured'; throw e; }

      const controller = 'AbortController' in window ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
      let res;
      try {
        res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'Idempotency-Key': payload.order_request_id },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
          signal: controller ? controller.signal : undefined
        });
      } catch (_) {
        const e = new Error('network'); e.code = 'network'; throw e;
      } finally {
        if (timer) clearTimeout(timer);
      }
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON */ }
      if (!res.ok || !data || data.ok !== true) { const e = new Error('server'); e.code = 'server'; e.status = res.status; throw e; }
      return data;
    }

    function successRecord(payload, cart, result) {
      return {
        reference: (result && (result.reference || result.order_request_id)) || payload.order_request_id,
        demo: !!(result && result.demo),
        created_at: payload.timestamp,
        customer: {
          full_name: payload.customer.full_name,
          phone: payload.customer.phone,
          governorate: payload.customer.governorate,
          city: payload.customer.city,
          address: payload.customer.address
        },
        items: cart.items.map((i) => ({
          title: i.product_title,
          variant: (i.options_with_values || []).filter((o) => o.value !== 'Default Title').map((o) => `${o.name}: ${o.value}`).join('، '),
          quantity: i.quantity,
          line_total: formatMoney(i.final_line_price)
        })),
        total: formatMoney(cart.total_price)
      };
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (submitting || finished) return;
      attempted = true;
      hideError();

      const invalid = FIELDS.filter((name) => !validateField(name));
      if (invalid.length) {
        const first = form.elements[invalid[0]];
        if (first) first.focus();
        toast(S.fixErrors, 'error');
        return;
      }

      submitting = true;
      setSubmitting(true);
      let payload = null;
      try {
        const cart = await Cart.get();
        if (!cart.item_count) { showError(S.emptyCartError); return; }
        payload = buildPayload(cart);
        if (payload.company) { finished = true; return; } // silently drop bots

        const result = await sendOrder(payload);
        store.set('izar:lastOrder', JSON.stringify(successRecord(payload, cart, result)));
        store.remove('izar:pendingRequestId');
        try {
          await fetch(ROOT + 'cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        } catch (_) { /* the order is already recorded; cart clearing is best-effort */ }
        finished = true;
        window.location.href = R.orderSuccess;
      } catch (err) {
        let message = S.submitError;
        if (err.code === 'not_configured') message = S.notConfigured;
        else if (err.code === 'network') message = S.networkError;
        showError(message, payload);
      } finally {
        submitting = false;
        if (!finished) setSubmitting(false);
      }
    });
  }

  /* ---------- Order success ---------- */
  function initSuccess() {
    const root = $('[data-order-success]');
    if (!root) return;
    let data = null;
    try { data = JSON.parse(store.get('izar:lastOrder') || 'null'); } catch (_) { data = null; }

    if (!data && IZ.designMode) {
      data = {
        reference: 'IZR-261003-7KQ2M', demo: true,
        customer: { full_name: 'أحمد محمد علي', phone: '01000000000', governorate: 'القاهرة', city: 'مدينة نصر', address: 'شارع الطيران – عمارة 12 – الدور 3 – شقة 8' },
        items: [{ title: 'إحرام إزار', variant: 'المقاس: L', quantity: 1, line_total: formatMoney(85000) }],
        total: formatMoney(85000)
      };
    }

    const set = (sel, text) => { const el = $(sel, root); if (el) el.textContent = text || ''; };

    if (!data) {
      set('[data-success-title]', S.successMissingTitle);
      set('[data-success-lead]', S.successMissingText);
      return;
    }

    set('[data-success-ref]', data.reference);
    set('[data-success-name]', data.customer && data.customer.full_name);
    set('[data-success-phone]', data.customer && data.customer.phone);
    set('[data-success-area]', data.customer ? [data.customer.governorate, data.customer.city].filter(Boolean).join(' — ') : '');
    set('[data-success-address]', data.customer && data.customer.address);
    set('[data-success-total]', data.total);

    const list = $('[data-success-items]', root);
    const tpl = $('[data-success-item-template]', root);
    if (list && tpl && Array.isArray(data.items)) {
      list.textContent = '';
      data.items.forEach((item) => {
        const node = tpl.content.firstElementChild.cloneNode(true);
        $('[data-name]', node).textContent = item.title;
        $('[data-meta]', node).textContent = [item.variant, `${S.quantityLabel || ''} ${item.quantity}`.trim()].filter(Boolean).join('، ');
        $('[data-price]', node).textContent = item.line_total;
        list.appendChild(node);
      });
    }
    const demo = $('[data-success-demo]', root);
    if (demo) demo.hidden = !data.demo;
    const details = $('[data-success-details]', root);
    if (details) details.hidden = false;
  }

  /* ---------- Boot ---------- */
  function init(scope = document) {
    $$('[data-product]', scope).forEach(initProduct);
  }

  init();
  initOrderForm();
  initSuccess();

  // Keep the cart fresh when returning via back/forward cache
  window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refresh().catch(() => {}); });

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
